/* Beardbrand – Variant / Badge controller (v5.2)
   - Works on PDP, featured-product sections, and bundle PDPs.
   - Multiple products per page supported.

   Priority:
     1) Sold-out / unavailable -> only .product-badge.oos
     2) Variant metafield badge (custom.product_badge_display_by_variant)
     3) Product-level tag badges

   - Variant metafield HTML comes from per-product JSON script tags:
     <script type="application/json" data-variant-metafields="true">...</script>
*/

(function () {
  if (window.__BB_VARIANT_BADGES_V52__) return;
  window.__BB_VARIANT_BADGES_V52__ = true;

  const PRODUCT_JSON_ID = 'product-json';

  // All variant metafields for the page, keyed by variant ID (string)
  let metafieldMap = {};

  // Cache of product JSON per root
  const productDataCache = new WeakMap();

  function loadMetafields() {
    try {
      const scripts = document.querySelectorAll(
        'script[data-variant-metafields]:not([data-bb-loaded])'
      );

      scripts.forEach(function (el) {
        const json = el.textContent || el.innerText || '{}';
        try {
          const parsed = JSON.parse(json);
          if (parsed && typeof parsed === 'object') {
            Object.assign(metafieldMap, parsed);
          }
          el.setAttribute('data-bb-loaded', 'true');
        } catch (e) {
          console.warn('[BB Variant Badges] Failed to parse metafield JSON from script', e);
        }
      });
    } catch (e) {
      console.warn('[BB Variant Badges] Error loading metafields', e);
    }
  }

  function loadProductData(root) {
    if (!root) return null;
    if (productDataCache.has(root)) {
      return productDataCache.get(root);
    }

    const local = root.querySelector && root.querySelector('#' + PRODUCT_JSON_ID);
    const global = document.getElementById(PRODUCT_JSON_ID);
    const el = local || global;
    if (!el) {
      productDataCache.set(root, null);
      return null;
    }

    try {
      const json = el.textContent || el.innerText || '{}';
      const parsed = JSON.parse(json);
      if (parsed && typeof parsed === 'object') {
        productDataCache.set(root, parsed);
        return parsed;
      }
    } catch (e) {
      console.warn('[BB Variant Badges] Failed to parse product JSON', e);
    }

    productDataCache.set(root, null);
    return null;
  }

  function getCurrentVariantInput(root) {
    if (!root) return null;
    return (
      root.querySelector('.product-variant__input[type="radio"]:checked') ||
      root.querySelector('input[type="radio"][name*="variant"][checked]')
    );
  }

  function getCurrentVariantId(root, variantInput) {
    if (variantInput) {
      const attr = variantInput.getAttribute('data-variant-id');
      if (attr) return String(attr);
    }

    const idInput =
      root.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      root.querySelector('form[action*="/cart/add"] select[name="id"]');
    if (idInput && idInput.value) {
      return String(idInput.value);
    }

    const productData = loadProductData(root);
    if (!productData || !Array.isArray(productData.variants)) return null;

    const selectedOptions = [];

    const optionGroups = root.querySelectorAll('.product-variant__container');
    if (optionGroups.length) {
      optionGroups.forEach(function (group) {
        const checked = group.querySelector('.product-variant__input[type="radio"]:checked');
        if (checked && typeof checked.value === 'string') {
          selectedOptions.push(checked.value.trim());
        }
      });
    }

    if (!selectedOptions.length) {
      const selects = root.querySelectorAll('.product-variants');
      selects.forEach(function (sel) {
        if (sel && typeof sel.value === 'string') {
          selectedOptions.push(sel.value.trim());
        }
      });
    }

    if (!selectedOptions.length) return null;

    const match = productData.variants.find(function (v) {
      if (!v || !Array.isArray(v.options)) return false;
      if (v.options.length !== selectedOptions.length) return false;
      for (let i = 0; i < selectedOptions.length; i++) {
        if (String(v.options[i]).trim() !== String(selectedOptions[i]).trim()) {
          return false;
        }
      }
      return true;
    });

    return match ? String(match.id) : null;
  }

  // UPDATED FUNCTION
  function detectSoldOutState(root, variantInput) {
    // 0) Primary truth: current variant.available from product JSON
    try {
      const vid = getCurrentVariantId(root, variantInput);
      const productData = loadProductData(root);
      if (vid && productData && Array.isArray(productData.variants)) {
        const v = productData.variants.find(function (vv) {
          return String(vv.id) === String(vid);
        });
        if (v && v.available === false) {
          return true;
        }
      }
    } catch (e) {
      console.warn('[BB Variant Badges] Error checking variant.available', e);
      // fall through to the other signals
    }

    // 1) Swatch-level flag (Split + bundle-availability)
    if (variantInput && variantInput.classList.contains('sold-out-swatch')) {
      return true;
    }

    // 2) ATC button disabled or labeled as sold out / unavailable
    const atc =
      root.querySelector('.product__add-to-cart') ||
      root.querySelector('.product-form__cart-submit') ||
      root.querySelector('button[name="add"]');

    if (atc) {
      const text = (atc.textContent || '').trim().toLowerCase();
      if (atc.classList.contains('disabled')) return true;
      if (atc.hasAttribute('disabled')) return true;
      if (/sold\s*out/.test(text)) return true;
      if (/unavailable/.test(text)) return true;
    }

    return false;
  }

  function updateBadgesForProduct(root) {
    if (!root) return;

    const header = root.querySelector('.product-header');
    if (!header) return;

    const allBadges = header.querySelectorAll('.product-badge');
    if (!allBadges.length) return;

    const soldOutBadge = header.querySelector('.product-badge.oos');
    const variantBadgeEl = header.querySelector('[data-variant-badge]');

    const productTagBadges = Array.prototype.filter.call(allBadges, function (el) {
      if (el === soldOutBadge) return false;
      if (variantBadgeEl && el === variantBadgeEl) return false;
      return true;
    });

    const variantInput = getCurrentVariantInput(root);
    const isSoldOut = detectSoldOutState(root, variantInput);

    allBadges.forEach(function (badge) {
      badge.style.display = 'none';
    });
    if (variantBadgeEl) {
      variantBadgeEl.innerHTML = '';
      variantBadgeEl.removeAttribute('aria-label');
    }

    // 1) Sold-out / unavailable
    if (isSoldOut) {
      if (soldOutBadge) {
        soldOutBadge.style.display = 'flex';
      }
      return;
    }

    // 2) Variant metafield badge vs product-tag badges
    loadMetafields();
    let variantHasBadge = false;

    if (variantBadgeEl && Object.keys(metafieldMap).length) {
      const vid = getCurrentVariantId(root, variantInput);
      const entry = vid ? metafieldMap[vid] : null;

      if (entry && entry.badge_html) {
        const html = entry.badge_html;
        variantBadgeEl.innerHTML = html;

        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const srText = (tmp.textContent || '').trim();

        if (srText) {
          variantBadgeEl.setAttribute(
            'aria-label',
            'This is a ' + srText + ' product.'
          );
        }

        variantBadgeEl.style.display = 'flex';
        variantHasBadge = true;
      }
    }

    if (variantHasBadge) {
      productTagBadges.forEach(function (badge) {
        badge.style.display = 'none';
      });
    } else {
      if (variantBadgeEl) {
        variantBadgeEl.style.display = 'none';
      }
      productTagBadges.forEach(function (badge) {
        badge.style.display = 'flex';
      });
    }
  }

  function wireProductPage(root) {
    if (!root || root.__bbBadgesWired) return;
    root.__bbBadgesWired = true;

    const handler = function () {
      updateBadgesForProduct(root);
    };

    // Variant changes via radios/selects
    root.addEventListener('change', function (evt) {
      const t = evt.target;
      if (!t || !t.classList) return;

      if (t.classList.contains('product-variant__input')) {
        handler();
      }
      if (t.matches && t.matches('.product-variants, select[name="id"]')) {
        handler();
      }
    });

    // Theme / app custom events
    root.addEventListener('VARIANT_CHANGE', handler);
    root.addEventListener('variant:change', handler);
    root.addEventListener('bb:variant:change', handler);

    // Bundle bridge events on forms
    root.addEventListener('bb:variant:available', handler, true);
    root.addEventListener('bb:variant:unavailable', handler, true);

    // ATC button mutations (text / disabled state)
    const atc =
      root.querySelector('.product__add-to-cart') ||
      root.querySelector('.product-form__cart-submit') ||
      root.querySelector('button[name="add"]');
    if (atc) {
      const mo = new MutationObserver(handler);
      mo.observe(atc, { attributes: true, childList: true, subtree: true });
    }

    handler();
  }

  function findRootForHeader(header) {
    if (!header || !(header instanceof HTMLElement)) return null;
    return (
      header.closest('product-page') ||
      header.closest('.product-page') ||
      header.closest('.product--section') ||
      header.closest('.product') ||
      header.closest('.shopify-section') ||
      header
    );
  }

  function boot() {
    loadMetafields();

    document.querySelectorAll('.product-header').forEach(function (header) {
      const root = findRootForHeader(header);
      wireProductPage(root);
    });

    const mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes &&
          m.addedNodes.forEach(function (node) {
            if (!(node instanceof HTMLElement)) return;

            if (node.matches && node.matches('.product-header')) {
              const root = findRootForHeader(node);
              wireProductPage(root);
            } else if (node.querySelector) {
              node.querySelectorAll('.product-header').forEach(function (hdr) {
                const root = findRootForHeader(hdr);
                wireProductPage(root);
              });
            }
          });
      });
    });

    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();